"use client";

// Lua-original primitive — NOT from the @ai-elements registry.
// Covers the "agent dropped a place into the conversation" case so apps
// don't reinvent map-preview cards per surface. Map tile rendering is
// pluggable: pass `map` to integrate Google / Mapbox / OSM, fall back
// to a static placeholder otherwise so consumers can adopt without any
// map SDK.

import { ArrowSquareOut } from "@phosphor-icons/react";
import type { HTMLAttributes, ReactNode } from "react";
import { Icon } from "@/lib/icon";
import { cn } from "@/lib/utils";

// Map placeholder pin — light `bg-ring/30` halo with a solid `bg-ring`
// inner dot. Matches Figma's purple Lua-ring marker for the muted-tile
// fallback when consumers don't pass a `map` render fn.
const LocationPin = () => (
  <div
    aria-hidden="true"
    className="flex size-5 items-center justify-center rounded-full bg-ring/30"
  >
    <div className="size-2 rounded-full bg-ring" />
  </div>
);

export type LocationCardCoords = {
  latitude: number;
  longitude: number;
};

export type LocationCardProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "onSelect"
> & {
  latitude: number;
  longitude: number;
  /** Place name — shown above the address (e.g. "Lua HQ"). */
  label?: string;
  /** Free-form address text. */
  address?: string;
  /**
   * Click handler for the whole card. Typically opens the location in
   * the consumer's preferred maps app. Receives `{ latitude, longitude }`.
   */
  onSelect?: (coords: LocationCardCoords) => void;
  /**
   * Render the map tile. Receives the coords so consumers can plug in
   * Google / Mapbox / OSM without the design system depending on a
   * specific SDK. Falls back to a muted placeholder if omitted.
   */
  map?: (coords: LocationCardCoords) => ReactNode;
};

export const LocationCard = ({
  latitude,
  longitude,
  label,
  address,
  onSelect,
  map,
  className,
  ...props
}: LocationCardProps) => {
  const coords: LocationCardCoords = { latitude, longitude };
  const interactive = typeof onSelect === "function";

  const handleClick = () => {
    onSelect?.(coords);
  };

  const tile = map?.(coords) ?? (
    <div className="relative flex h-[140px] items-center justify-center bg-muted">
      <LocationPin />
    </div>
  );

  // When only coords render (no label, no address), Figma's `coords-only`
  // variant has zero inner gap. Otherwise the stack uses gap-1 (4px).
  const hasContent = Boolean(label || address);

  return (
    <div
      data-slot="location-card"
      className={cn(
        "flex w-80 flex-col overflow-hidden rounded-lg border border-border bg-card text-card-foreground",
        interactive &&
          "cursor-pointer transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring",
        className,
      )}
      onClick={interactive ? handleClick : undefined}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      {...props}
    >
      {tile}
      <div className={cn("flex w-full flex-col p-4", hasContent && "gap-1")}>
        {label ? (
          <div className="flex w-full items-center gap-2">
            <p className="min-w-0 flex-1 truncate font-medium text-base text-card-foreground">
              {label}
            </p>
            {interactive ? (
              <Icon
                className="shrink-0 text-muted-foreground"
                icon={ArrowSquareOut}
              />
            ) : null}
          </div>
        ) : null}
        {address ? (
          <p className="font-normal text-muted-foreground text-sm">{address}</p>
        ) : null}
        <p className="font-normal text-muted-foreground text-sm">
          {latitude.toFixed(4)}, {longitude.toFixed(4)}
        </p>
      </div>
    </div>
  );
};

LocationCard.displayName = "LocationCard";
